import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import { Box } from "@mui/material";
import * as React from "react";
import { ContentPagePanelType, EnumHelper, ReportSharingType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { FrontToDoItems, IFrontToDoItems } from "source/models/front/frontToDoItems";
import { IPagePanel } from "source/models/common/pagePanel";
import { IListFilterItem } from "source/models/common/listFilterItem";
import { IReportItem, ReportItem } from "source/models/report/reportItem";
import { IChartItem } from "source/models/chart/chartItem";
import { PageTitle } from "../PageTitle";
import { SettingsButton } from "../../Button/SettingsButton";
import { DashboardContainer } from "../../Dashboard/DashboardContainer";
import { PanelContainer } from "../../Panel/PanelContainer";
import { DashboardStatistics } from "../../DashboardStatistics";
import { DashboardPanelFiltersReports } from "source/components/app/DashboardPanelFiltersReports";
import { ISavedChartPanel } from "source/models/chart/savedChartPanel";
import { AppUtils } from "source/framework/appUtils";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { IFrontToDoItem } from "source/models/front/frontToDoItem";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";

type DashBoardChartProps = {
    companyId?: string;
    chartPanel: ISavedChartPanel;
    chartItems?: IChartItem[]; //Report based charts
    taskTypes?: ITaskEditTypeItem[]; //Task type based charts
    onShowChartPanelPropertiesDialog: (panelId: string) => void;
};

// PageDashboard
export interface IPageDashboardProps {
    settingsVisible: boolean;
    panels: IPagePanel[];
    companyId: string;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    loadingReports: boolean;
    title: string;
    reports: IReportItem[];
    savedReports: IReportItem[];
    chartItems: IChartItem[];
    taskTypes?: ITaskEditTypeItem[];
    dashboardChart: React.ElementType<DashBoardChartProps>;
    addButton?: React.ReactNode;
    onSavedFilterClick: (filterId: string) => void;
    onFilterClick: (filterId: string) => void;
    onSavedReportClick: (reportId: string) => void;
    onReportClick: (reportId: string) => void;
    onShowChartPanelPropertiesDialog: (panelId: string) => void;
    onToggleMenuPanel: () => void;
    onToggleSettingsDialog: () => void;
    onGetToDoItems: () => Promise<FrontToDoItems>;
    onStatisticsClick: (toDoItem: IFrontToDoItem) => void;
}

export interface IPageDashboardState {
    loadingStatistics: boolean;
    toDoItems: IFrontToDoItems;
    standardAndSharedReports: IReportItem[];
}

export class PageDashboard extends React.Component<IPageDashboardProps, IPageDashboardState> {
    _isMounted: boolean;

    constructor(props: IPageDashboardProps) {
        super(props);
        this.state = {
            loadingStatistics: true,
            toDoItems: null,
            standardAndSharedReports: ReportItem.getSharedAndStandardReports(props.reports, props.savedReports.filter(i => i.reportSharingType > ReportSharingType.NotShared))
        };
    }

    getRecordStatistics = async () => {
        if (!this.props.onGetToDoItems) return;
        this.setState({
            loadingStatistics: true
        });
        const toDoItems = await AppUtils.callService(() => this.props.onGetToDoItems(), false, true, () => {
            if (!this._isMounted) return;
            this.setState({
                loadingStatistics: false
            });
        });
        if (!toDoItems || !this._isMounted) return;
        this.setState({
            toDoItems
        });
    };
    
    async componentDidMount(): Promise<void> {
        this.getRecordStatistics();
        this._isMounted = true;
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps: IPageDashboardProps, prevState: IPageDashboardState): void {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ?? "";
        const companyId = props.companyId ?? "";
        if (ReportItem.getItemsHash(prevProps.reports, prevProps.savedReports) !== ReportItem.getItemsHash(props.reports, props.savedReports)) {
            this.setState({
                standardAndSharedReports: ReportItem.getSharedAndStandardReports(props.reports, props.savedReports.filter(i => i.reportSharingType > ReportSharingType.NotShared))
            });
        }
        if (prevCompanyId === companyId) {
            return;
        }
        this.getRecordStatistics();
    }
    
    handleReportClick = (reportId: string) => {
        const props = this.props;
        const state = this.state;
        const report = state.standardAndSharedReports.find(i => i.id === reportId);
        if (!report) return;
        if (report.reportSharingType > ReportSharingType.NotShared) {
            props.onSavedReportClick(reportId);
        } else {
            props.onReportClick(reportId);
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const DashboardChart = props.dashboardChart;
        return (
            <Box display="flex" flexDirection="column" sx={{ margin: "0 1rem" }}>
                <PageTitle title={props.title}>
                    {props.settingsVisible
                        ? (
                            <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "24px" }}>
                                {Translations.Settings}
                            </SettingsButton>
                        )
                        : null
                    }
                    {props.addButton}
                    <AppButton variant="contained" startIcon={<InsertChartOutlinedRoundedIcon />} onClick={props.onToggleMenuPanel}>
                        {Translations.Panels}
                    </AppButton>
                </PageTitle>
                <DashboardContainer>
                    {props.panels.map(panel => {
                        if (EnumHelper.isEqual(panel.panelType, ContentPagePanelType.Statistics) && panel.visible) {
                            return <PanelContainer key={panel.getId()} size={panel.size}>
                                <DashboardStatistics
                                    loading={state.loadingStatistics}
                                    title={panel.getTitle()}
                                    toDoItems={state.toDoItems}
                                    onClick={props.onStatisticsClick}
                                />
                            </PanelContainer>;
                        } else if (EnumHelper.isEqual(panel.panelType, ContentPagePanelType.MyFiltersAndReports) && panel.visible) {
                            return <PanelContainer key={panel.getId()} size={panel.size}>
                                <DashboardPanelFiltersReports
                                    loading={props.loadingReports}
                                    title={panel.getTitle()}
                                    filters={props.savedFilters.filter(i => !i.isShared)}
                                    reports={props.savedReports.filter(i => !i.getIsShared())}
                                    noFiltersMessage={Translations.NoFilters}
                                    noReportsMessage={Translations.NoReports}
                                    onFilterClick={props.onSavedFilterClick}
                                    onReportClick={props.onSavedReportClick}
                                />
                            </PanelContainer>;
                        } else if (EnumHelper.isEqual(panel.panelType, ContentPagePanelType.StandardFiltersAndReports) && panel.visible) {
                            return <PanelContainer key={panel.getId()} size={panel.size}>
                                <DashboardPanelFiltersReports
                                    loading={props.loadingReports}
                                    title={panel.getTitle()}
                                    filters={props.filters}
                                    reports={state.standardAndSharedReports}
                                    noFiltersMessage={Translations.NoFilters}
                                    noReportsMessage={Translations.NoReports}
                                    onFilterClick={props.onFilterClick}
                                    onReportClick={this.handleReportClick}
                                />
                            </PanelContainer>;
                        } else if (EnumHelper.isEqual(panel.panelType, ContentPagePanelType.Chart) && panel.visible && !panel.getDisabled()) {
                            return <PanelContainer key={panel.getId()} size={panel.size}>
                                <DashboardChart
                                    companyId={props.companyId}
                                    chartPanel={panel as ISavedChartPanel}
                                    chartItems={props.chartItems}
                                    taskTypes={props.taskTypes}
                                    onShowChartPanelPropertiesDialog={props.onShowChartPanelPropertiesDialog}
                                />
                            </PanelContainer>;
                        } else {
                            return null;
                        }
                    })}
                </DashboardContainer>
            </Box>
        );
    }
}